
import { useMutation, useQuery } from "@tanstack/react-query";
import { taskService } from "services/taskService";
import { FormAttachmentTask, FormDetachTask, FormTaskStore, FormTaskUpdate, IdForm, inputQueryTask } from "types";
import { useTag } from "./useTag";
import { useSprint } from "./useSprint";
import SnackbarUtils from "utils/SnackbarUtils";

export function useTask() {
    const { getList: getListSprint } = useSprint()
    const { getList: getListTag } = useTag()
    const getDetail = async (projectId, id) => {
        if (!projectId || !id) return { data: null };

        const data = await taskService.show(projectId, id);
        return data;
    };

    const fetchDataDetail = (projectId, id, isFetch = 1) => {
        return useQuery({
            queryKey: ['taskDetail', projectId, id, isFetch],
            queryFn: () => getDetail(projectId, id),
            select: (data) => ({
                data: data?.data ?? null,
            }),
            enabled: !!projectId && !!id,
            staleTime: 1000 * 60 * 3,
        });
    }

    const getList = async (projectId, page = 1) => {
        const data = await taskService.onGetList(projectId, page);
        return data;
    };

    const fetchData = (projectId, currentPage, isFetch = 1) => {
        return useQuery({
            queryKey: ['tasks', projectId, currentPage, isFetch],
            queryFn: () => getList(projectId, currentPage),
            enabled: !!projectId && currentPage !== isFetch,
            staleTime: 1000 * 60 * 3,
            select: (data) => ({
                data: data?.data ?? [],
                totalPage: data?.totalPage ?? 1,
            }),
        });
    }

    const onSearch = async (projectId, input: inputQueryTask) => {
        const data = await taskService.onSearch(projectId, input);
        return data;
    };

    const fetchDataSearch = (projectId, input: inputQueryTask) => {
        if (!projectId) return { data: null };
        return useQuery({
            queryKey: ['tasksSearch', projectId, `${input}`],
            queryFn: () => onSearch(projectId, input),
            staleTime: 1000 * 60 * 3,
            enabled: !!projectId,
            select: (data) => ({
                data: data?.data ?? [],
                totalPage: data?.totalPage ?? 1,
            }),
        });
    }

    const createTask = useMutation({
        mutationFn: (payload: FormTaskStore) => taskService.store(payload),
        onSuccess: async (res) => res,
        onError: (err) => {
            console.error("createTask failed", err);
        },
    });

    const updateTask = useMutation({
        mutationFn: (formData: FormTaskUpdate) => taskService.update(formData),
        onSuccess: async (res) => {
            if (res?.statusCode !== 200) {
                SnackbarUtils.error(res?.message ?? "Update task failure!");
            }
            return res;
        },
        onError: (err) => {
            console.error("updateTask failed", err);
        },
    });

    const attachmentTask = useMutation({
        mutationFn: (formData: FormAttachmentTask) => taskService.attachment(formData),
        onSuccess: async (res) => {
            if (res?.statusCode !== 200) {
                SnackbarUtils.error(res?.message ?? "Attachment file failure!");
            } else {
                SnackbarUtils.success(res?.message ?? "Attachment file success!");
            }
            return res;
        },
        onError: (err) => {
            console.error("updateTask failed", err);
        },
    });

    const detach = useMutation({
        mutationFn: (formData: FormDetachTask) => taskService.detach(formData),
        onSuccess: async (res) => {
            if (res?.statusCode !== 200) {
                SnackbarUtils.error(res?.message ?? "Detach task failure!");
            } else {
                SnackbarUtils.success(res?.message ?? "Detach task success!");
            }
            return res;
        },
        onError: (err) => {
            console.error("updateTask failed", err);
        },
    });

    const delTask = useMutation({
        mutationFn: (formData: IdForm) => taskService.destroy(formData),
        onSuccess: async (res) => {
            if (res?.statusCode !== 200) {
                SnackbarUtils.error(res?.message ?? "Delete task failure!");
            } else {
                SnackbarUtils.success(res?.message ?? "Delete task success!");
            }
            return res;
        },
        onError: (err) => {
            console.error("delTask failed", err);
        },
    });

    const loadDataDepend = async (projectId) => {
        try {
            const [sprints, tags] = await Promise.all([
                getListSprint(projectId),
                getListTag(projectId)
            ]);
            return { sprints, tags }
        } catch (error) {
            console.error("Lỗi load dữ liệu:", error);
        }
    }

    return {
        fetchData,
        fetchDataDetail,
        fetchDataSearch,
        loadDataDepend,
        onSearch,
        getDetail,
        createTask,
        updateTask,
        attachmentTask,
        detach,
        delTask
    }
}
